import {
  CustomRate,
  FIRST_LAST_WEEK_OPTIONS,
  FIRST_WEEK_DAYS_OPTIONS,
  LAST_WEEK_DAYS_OPTIONS,
  REPEAT_SELECT_OPTIONS,
  getAdvancedCronStringFromConfig,
  getConfigFromAdvancedCronString,
} from "components/ACAdvancedCron";
import _, { isArray, split, uniq } from "lodash";
import moment from "moment";
import { NotificationReportJpaDto } from "types/api/notificationReportJpaDto";
import { find, map } from "utils/lodashUtils";
import { CellRenderers } from "utils/tableUtils";

export const convertAdvancedACCronString = ({
  cron,
  displayInTimeZone = false,
}: {
  cron: string;
  displayInTimeZone?: boolean;
}) => {
  if (displayInTimeZone) {
    return convertAdvancedCronToLocalTimeZone(cron);
  } else {
    return convertAdvancedCronToUTC(cron);
  }
};

function convertAdvancedCronToLocalTimeZone(cron: string) {
  const { time, daysOfTheWeek, weekNumber, dates, months, repeatOption } =
    getConfigFromAdvancedCronString(cron);
  const utcOffset = time?.utcOffset();

  const localDaysOfTheWeek = map(daysOfTheWeek, (dayInUTC) => {
    // dayInUTC starts from 1, but moment considers from 0
    const utcDate = moment(time)
      .day(dayInUTC - 1)
      .add(utcOffset, "minutes");
    return utcDate.day() + 1;
  });

  const localTime = moment(time).add(utcOffset, "minutes");

  return getAdvancedCronStringFromConfig({
    time: localTime,
    daysOfTheWeek: localDaysOfTheWeek,
    weekNumber,
    dates,
    months,
    repeatOption,
  });
}

function convertAdvancedCronToUTC(cron: string) {
  const { time, daysOfTheWeek, weekNumber, dates, months, repeatOption } =
    getConfigFromAdvancedCronString(cron);
  const utcOffset = time?.utcOffset();

  const UTCDaysOfTheWeek = map(daysOfTheWeek, (dayInUTC) => {
    // dayInUTC starts from 1, but moment considers from 0
    const localDate = moment(time)
      .day(dayInUTC - 1)
      .subtract(utcOffset, "minutes");
    return localDate.day() + 1;
  });

  const UTCTime = moment(time).subtract(utcOffset, "minutes");

  return getAdvancedCronStringFromConfig({
    time: UTCTime,
    daysOfTheWeek: UTCDaysOfTheWeek,
    weekNumber,
    dates,
    months,
    repeatOption,
  });
}

export const getNewCronString = (
  schedule: CustomRate & { cronExpressions: string[] },
) => {
  if (schedule.type === "FIXED_RATE") return getCustomReapeatString(schedule);
  const cronString = reverseModifyCronData(schedule.cronExpressions, null);
  return cronString
    ? getFormattedAdvancedCronSummaryString(
        convertAdvancedACCronString({
          cron: cronString,
          displayInTimeZone: true,
        }),
        true,
      )
    : "";
};

export const getCustomReapeatString = ({
  fixedRateUnit,
  fixedRateValue,
  startAt,
}: CustomRate) => {
  return `Every ${fixedRateValue} ${_.capitalize(fixedRateUnit)} starting from ${moment(
    startAt,
  ).format("YYYY-MM-DD hh:mm a")}`;
};

export const getFormattedAdvancedCronSummaryString = (
  cronString: string | undefined,
  stringResult: boolean = true,
) => {
  if (!cronString) {
    return CellRenderers.VALUE_OR_NA();
  }
  const {
    time,
    daysOfTheWeek,
    dates,
    months,
    repeatOption,
    weekNumber,
    isFirstLastWeek,
  } = getConfigFromAdvancedCronString(cronString);
  const dayShortHands: { [key: number]: string } = {
    1: "Sun",
    2: "Mon",
    3: "Tue",
    4: "Wed",
    5: "Thu",
    6: "Fri",
    7: "Sat",
  };
  let dayString;

  const formattedTime = time?.format("hh:mm a");
  const repeatOptionString = find(
    REPEAT_SELECT_OPTIONS,
    (opt) => opt.value === repeatOption,
  )?.label;
  let resultString;
  if (repeatOption === "everyWeek") {
    dayString = daysOfTheWeek.map((day) => dayShortHands[day]).join(", ");
    resultString = `Every ${dayString} at ${formattedTime}`;
  }
  if (repeatOption === "everyMonth") {
    dayString = dates
      ?.map(
        (date) =>
          find([...FIRST_WEEK_DAYS_OPTIONS, ...LAST_WEEK_DAYS_OPTIONS], {
            value: date,
          })?.label,
      )
      .join(", ");
    const weekNumberString = find(FIRST_LAST_WEEK_OPTIONS, {
      value: isFirstLastWeek,
    })?.label;
    resultString = `${repeatOptionString}, ${weekNumberString}, on ${dayString} at ${formattedTime}`;
  }

  if (repeatOption === "specificWeek") {
    const uniqueWeeks = uniq(
      map(
        weekNumber,
        (week) =>
          `${find(FIRST_WEEK_DAYS_OPTIONS, { value: week })?.label} Week`,
      ),
    );

    const uniqueDays = uniq(daysOfTheWeek.map((day) => dayShortHands[day]));

    const weekString = uniqueWeeks.join(", ");
    const dayString = uniqueDays.join(", ");

    resultString = `Every ${weekString}, ${dayString} at ${formattedTime}`;
  }

  if (repeatOption === "specificMonth") {
    dayString = `${months?.join(", ")} ${dates
      ?.map(
        (date) =>
          find([...FIRST_WEEK_DAYS_OPTIONS, ...LAST_WEEK_DAYS_OPTIONS], {
            value: date,
          })?.label,
      )
      .join(", ")}`;
    resultString = `Every year, ${dayString} at ${formattedTime}`;
  }

  return stringResult
    ? resultString
    : { formattedTime, dayString, repeatOption };
};

export const modifyCronData = (data: NotificationReportJpaDto) => {
  let cronExpressions;
  const { cron } = data;
  //specific week case
  if (cron?.includes("#")) {
    const cronConfig = cron.split(" ");
    const [second, minute, hour, dayOfTheMonth, month, daysOfTheWeekString] =
      cronConfig;
    cronExpressions = map(
      daysOfTheWeekString.split(","),
      (weekStr) =>
        `${second} ${minute} ${hour} ${dayOfTheMonth} ${month} ${weekStr}`,
    );
  } else {
    cronExpressions = !cron ? null : [cron];
  }
  const modifiedData = {
    ...data,
    cron: null,
    cronExpressions,
  };
  return modifiedData;
};

export const reverseModifyCronData = (
  cronExpressions: string[] | undefined,
  cron: string | null,
) => {
  if (cron) {
    return cron;
  }
  if (_.isEmpty(cronExpressions)) return undefined;
  const [second, minute, hour, dayOfTheMonth, month] = isArray(cronExpressions)
    ? cronExpressions[0].split(" ")
    : split(cron, " ");

  //specific week case
  if (cronExpressions && cronExpressions.length > 1) {
    const daysOfTheWeek = map(
      cronExpressions,
      (expression) => expression.split(" ")[5],
    );
    const originalCron = `${second} ${minute} ${hour} ${dayOfTheMonth} ${month} ${daysOfTheWeek.join(
      ",",
    )}`;
    return originalCron;
  }

  return cronExpressions?.[0];
};
